import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {App} from "antd";
import {currentUserActions, currentUserSelectors} from "@features/currentUser";
import {displayNotification} from "@sfs/utils/displayNotification";

/**
 * Nudge connected users whose email address is not verified: offer them to
 * receive a login link, which validates the address when they use it.
 * The notification key lets antd deduplicate it: it only (re)appears when the
 * connected/emailVerified values change.
 */
export const useEmailVerificationNudge = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {message, notification} = App.useApp();
  const connected = useSelector(currentUserSelectors.selectConnected);
  const authenticatedUser = useSelector(currentUserSelectors.selectAuthenticatedUser);
  const emailVerified = authenticatedUser?.emailVerified;

  useEffect(() => {
    if (connected && emailVerified === false) {
      displayNotification("info", "emailVerificationNudge", {
        message: t("common:connectionPage.magicLink.nudgeTitle"),
        description: t("common:connectionPage.magicLink.nudgeBody"),
        buttonText: t("common:connectionPage.magicLink.nudgeButton"),
        onClickButton: async () => {
          // Explicit email: the authenticated user's own, not the slice's user one
          // (they differ on inscription-front when impersonating)
          const sent = await dispatch(
            currentUserActions.sendLoginLink(undefined, authenticatedUser.email)
          );
          if (sent) message.success(t("common:connectionPage.magicLink.nudgeSentAgain"));
        },
      });
    }

    // Take the notification off the screen when the user logs out or verified since
    return () => notification.destroy("emailVerificationNudge");
  }, [connected, emailVerified]);
};
